import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Card,
  CardBody,
  CardHeader,
  Badge,
  Button,
  Alert,
  Modal,
  ModalHeader,
  ModalBody
} from 'reactstrap';
import Swal from 'sweetalert2';
import { renderDraftContent } from '../../util/draftToHtml';
import {
  approveGeneratedQuestion,
  rejectGeneratedQuestion,
  editGeneratedQuestion,
  clearGeneratedQuestions
} from '../../redux/QuestionBank/AIGenerationAction';
import QuestionBuilder from './QuestionBuilder';

const AIGeneratedQuestionsList = ({ userEmail }) => {
  const dispatch = useDispatch();

  // Redux state
  const { generatedQuestions, generatedPassage, loading } = useSelector(state => state.aiGeneration);
  const { subjects } = useSelector(state => state.questionBankSubjects);
  const { units } = useSelector(state => state.questionBankUnits);

  // Local state
  const [editingQuestion, setEditingQuestion] = useState(null);
  const [editModal, setEditModal] = useState(false);

  // M10: Use shared helper (with M9 DOMPurify sanitization)
  const renderQuestionPreview = (rawContent) => renderDraftContent(rawContent);

  const getChoiceLabel = (index) => {
    return String.fromCharCode(65 + index);
  };

  const getSubjectName = (subjectId) => {
    const subject = subjects.find(s => s.subjectId === subjectId);
    return subject ? subject.name : 'Unknown';
  };

  const getUnitName = (unitId) => {
    const unit = units.find(u => u.unitId === unitId);
    return unit ? unit.name : 'Unknown';
  };

  const handleApprove = async (question) => {
    const confirm = await Swal.fire({
      title: 'Approve Question?',
      text: 'This will add the question to your question bank.',
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: '#5e72e4',
      cancelButtonColor: '#8898aa',
      confirmButtonText: 'Yes, approve it!'
    });

    if (confirm.isConfirmed) {
      Swal.showLoading();
      const subjectName = subjects?.find(s => s.subjectId === question.subjectId)?.name;
      const result = await dispatch(approveGeneratedQuestion(question, generatedPassage, userEmail, subjectName));
      Swal.close();

      if (result.success) {
        Swal.fire('Approved!', 'Question has been added to your question bank.', 'success');
      } else {
        Swal.fire('Error', result.error, 'error');
      }
    }
  };

  const handleReject = async (tempId) => {
    const confirm = await Swal.fire({
      title: 'Reject Question?',
      text: 'This question will be removed from the generated list.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#f5365c',
      cancelButtonColor: '#8898aa',
      confirmButtonText: 'Yes, reject it!'
    });

    if (confirm.isConfirmed) {
      dispatch(rejectGeneratedQuestion(tempId));
      Swal.fire('Rejected!', 'Question has been removed.', 'success');
    }
  };

  const handleEdit = (question) => {
    setEditingQuestion(question);
    setEditModal(true);
  };

  const handleApproveAll = async () => {
    const confirm = await Swal.fire({
      title: 'Approve All Questions?',
      text: `This will add all ${generatedQuestions.length} questions to your question bank.`,
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: '#5e72e4',
      cancelButtonColor: '#8898aa',
      confirmButtonText: 'Yes, approve all!'
    });

    if (confirm.isConfirmed) {
      Swal.fire({
        title: 'Approving Questions',
        text: 'Please wait...',
        allowOutsideClick: false,
        didOpen: () => {
          Swal.showLoading();
        }
      });

      let successCount = 0;
      let failCount = 0;

      for (const question of generatedQuestions) {
        const subjectName = subjects?.find(s => s.subjectId === question.subjectId)?.name;
        const result = await dispatch(approveGeneratedQuestion(question, generatedPassage, userEmail, subjectName));
        if (result.success) {
          successCount++;
        } else {
          failCount++;
        }
      }

      Swal.close();
      Swal.fire(
        'Complete!',
        `Approved ${successCount} questions. ${failCount > 0 ? `Failed: ${failCount}` : ''}`,
        successCount > 0 ? 'success' : 'error'
      );
    }
  };

  const handleClearAll = async () => {
    const confirm = await Swal.fire({
      title: 'Clear All Questions?',
      text: 'This will remove all generated questions. This action cannot be undone.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#f5365c',
      cancelButtonColor: '#8898aa',
      confirmButtonText: 'Yes, clear all!'
    });

    if (confirm.isConfirmed) {
      dispatch(clearGeneratedQuestions());
      Swal.fire('Cleared!', 'All generated questions have been removed.', 'success');
    }
  };

  if (generatedQuestions.length === 0) {
    return (
      <Card className="shadow mt-4">
        <CardBody>
          <Alert color="info" className="mb-0">
            <i className="fas fa-info-circle mr-2"></i>
            No generated questions yet. Use the form above to generate questions with AI.
          </Alert>
        </CardBody>
      </Card>
    );
  }

  return (
    <>
      <Card className="shadow mt-4">
        <CardHeader className="bg-transparent">
          <div className="d-flex justify-content-between align-items-center">
            <div>
              <h3 className="mb-0">Generated Questions - Review & Approve</h3>
              <p className="text-sm text-muted mb-0 mt-1">
                {generatedQuestions.length} question{generatedQuestions.length !== 1 ? 's' : ''} pending review
              </p>
            </div>
            <div>
              <Button
                color="success"
                size="sm"
                className="mr-2"
                onClick={handleApproveAll}
                disabled={loading}
              >
                <i className="fas fa-check-double mr-2"></i>
                Approve All
              </Button>
              <Button
                color="danger"
                size="sm"
                onClick={handleClearAll}
                disabled={loading}
              >
                <i className="fas fa-trash-alt mr-2"></i>
                Clear All
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardBody>
          {/* Display Passage if exists */}
          {generatedPassage && (
            <Card className="mb-4 border-primary">
              <CardBody>
                <div className="d-flex align-items-center mb-3">
                  <Badge color="primary" className="mr-2">
                    <i className="fas fa-book-open mr-1"></i>
                    Generated Passage
                  </Badge>
                  {generatedPassage.figureUrl && (
                    <Badge color="success" className="mr-2">
                      <i className="fas fa-image mr-1"></i>
                      Includes Figure
                    </Badge>
                  )}
                  <h4 className="mb-0">{generatedPassage.title}</h4>
                </div>

                {/* Display Figure if exists */}
                {generatedPassage.figureUrl && (
                  <div className="mb-3 text-center">
                    <img
                      src={generatedPassage.figureUrl}
                      alt={generatedPassage.figureDescription || 'Generated figure'}
                      style={{
                        maxWidth: '100%',
                        maxHeight: '400px',
                        border: '1px solid #dee2e6',
                        borderRadius: '8px'
                      }}
                    />
                    {generatedPassage.figureDescription && (
                      <p className="text-muted mt-2 mb-0">
                        <small><strong>Figure:</strong> {generatedPassage.figureDescription}</small>
                      </p>
                    )}
                  </div>
                )}

                <div className="passage-content">
                  {renderQuestionPreview(generatedPassage.passageText)}
                </div>
                <Alert color="info" className="mt-3 mb-0">
                  <i className="fas fa-info-circle mr-2"></i>
                  All questions below are based on this passage. The passage will be saved automatically when you approve the first question.
                </Alert>
              </CardBody>
            </Card>
          )}

          {generatedQuestions.map((question, index) => (
            <Card key={question.tempId} className="mb-3 border">
              <CardBody>
                <div className="d-flex justify-content-between align-items-start mb-3">
                  <div>
                    <Badge color="info" className="mr-2">Question {index + 1}</Badge>
                    {question.isPassageBased && (
                      <Badge color="primary" className="mr-2">
                        <i className="fas fa-book-open mr-1"></i>
                        Passage-based
                      </Badge>
                    )}
                    <Badge color="primary" className="mr-2">{getSubjectName(question.subjectId)}</Badge>
                    <Badge color="success" className="mr-2">{getUnitName(question.unitId)}</Badge>
                    {/* Difficulty badge hidden for now */}
                  </div>
                  <div>
                    <Button
                      color="success"
                      size="sm"
                      className="mr-2"
                      onClick={() => handleApprove(question)}
                      disabled={loading}
                    >
                      <i className="fas fa-check mr-1"></i>
                      Approve
                    </Button>
                    <Button
                      color="warning"
                      size="sm"
                      className="mr-2"
                      onClick={() => handleEdit(question)}
                      disabled={loading}
                    >
                      <i className="fas fa-edit mr-1"></i>
                      Edit
                    </Button>
                    <Button
                      color="danger"
                      size="sm"
                      onClick={() => handleReject(question.tempId)}
                      disabled={loading}
                    >
                      <i className="fas fa-times mr-1"></i>
                      Reject
                    </Button>
                  </div>
                </div>

                <div className="mb-3">
                  <h5 className="mb-2">Question:</h5>
                  {renderQuestionPreview(question.questionText)}
                </div>

                <div className="mb-3">
                  <h5 className="mb-2">Choices:</h5>
                  {question.choices?.map((choice, idx) => (
                    <div key={idx} className="mb-2 d-flex align-items-start">
                      <Badge
                        color={question.correctAnswer?.includes(choice.choiceId) ? 'success' : 'light'}
                        className="mr-2"
                      >
                        {question.correctAnswer?.includes(choice.choiceId) ? '✓' : getChoiceLabel(idx)}
                      </Badge>
                      <div className="flex-grow-1">
                        {renderQuestionPreview(choice.text)}
                      </div>
                    </div>
                  ))}
                </div>

                <div>
                  <h5 className="mb-2">Explanation:</h5>
                  {renderQuestionPreview(question.explanationText)}
                </div>
              </CardBody>
            </Card>
          ))}
        </CardBody>
      </Card>

      {/* Edit Modal */}
      <Modal isOpen={editModal} toggle={() => setEditModal(false)} size="xl" scrollable>
        <ModalHeader toggle={() => setEditModal(false)}>
          Edit Generated Question
        </ModalHeader>
        <ModalBody>
          {editingQuestion && (
            <>
              <Alert color="info" className="mb-3">
                <strong>Edit & Save:</strong> Make your changes below and click "Update Question".
                The edited version will replace the AI-generated one in the review list. You can then approve it.
              </Alert>
              <QuestionBuilder
                userEmail={userEmail}
                editingQuestion={editingQuestion}
                onEditSave={(updatedQuestionData) => {
                  // Update the generated question in Redux state (not the database)
                  const updatedQuestion = {
                    ...editingQuestion,
                    ...updatedQuestionData,
                    source: 'ai_generated',
                    status: 'pending_review'
                  };
                  dispatch(editGeneratedQuestion(editingQuestion.tempId, updatedQuestion));
                  setEditModal(false);
                  setEditingQuestion(null);
                  Swal.fire('Updated!', 'Question has been updated. You can now approve it.', 'success');
                }}
                onQuestionSaved={() => {
                  setEditModal(false);
                  setEditingQuestion(null);
                }}
              />
            </>
          )}
        </ModalBody>
      </Modal>
    </>
  );
};

export default AIGeneratedQuestionsList;
